
import Vue from 'vue';
import SimpleFiltersCard from '@/components/filters/SimpleFiltersCard.vue';
import EntityComboFilter from '@/components/filters/EntityComboFilter.vue';

type EntityScopeKey = 'device' | 'property' | 'connector' | 'view';
type AdvancedOptionKey = 'reading' | 'regex' | 'dsl';

type RegexLocalState = { attribute?: string; regex?: string };
type DslLocalState = { dsl?: string };

type ScopeLocalFilters = {
  device: any;
  property: any;
  connector: any;
  view: any;
  reading: any;
  regex: RegexLocalState;
  dsl: DslLocalState;
};

type ScopeGroupUiState = {
  localFilters: ScopeLocalFilters;
  entityMenuOpen: Record<EntityScopeKey, boolean>;
  moreMenuOpen: boolean;
  enabledAdvancedOptions: AdvancedOptionKey[];
  advancedMenuOpen: Record<AdvancedOptionKey, boolean>;
};

export default Vue.extend({
  name: 'RuleScopeFiltersCard',
  components: { SimpleFiltersCard },

  props: {
    value: { type: [Object, Array], required: true },
    tags: { type: Array, required: false, default: () => [] },
  },

  data() {
    return {
      isHydratingFromIncomingValue: false,
      lastEmittedPayload: null as string | null,

      entityScopeKeys: [
        'device',
        'property',
        'connector',
        'view',
      ] as EntityScopeKey[],

      scopeGroups: [] as ScopeGroupUiState[],

      advancedOptions: [
        {
          key: 'reading' as AdvancedOptionKey,
          label: this.$t('rule.advanced.reading') as string,
        },
        {
          key: 'regex' as AdvancedOptionKey,
          label: this.$t('rule.advanced.regex') as string,
        },
        {
          key: 'dsl' as AdvancedOptionKey,
          label: this.$t('rule.advanced.dsl') as string,
        },
      ],

      entityFiltersConfig: [
        { attribute: 'id', always: true },
        { attribute: 'tagIds', always: true },
        { attribute: 'createdAt', always: true },
        { attribute: 'withDeleted', always: true },
        { attribute: 'deletedAt', always: true },
      ],

      readingFiltersConfig: [
        { attribute: 'timestamp', always: true },
        { attribute: 'flag', always: true },
        { attribute: 'id', always: true },
        { attribute: 'createdAt', always: true },
        { attribute: 'withDeleted', always: true },
        { attribute: 'deletedAt', always: true },
      ],

      regexFiltersConfig: [
        { attribute: 'ruleRegexAttribute', always: true },
        { attribute: 'ruleRegexPattern', always: true },
      ],

      dslFiltersConfig: [{ attribute: 'ruleDslExpression', always: true }],
    };
  },

  created() {
    this.hydrateUiFromValue((this as any).value);
  },

  methods: {
    getSummary(scopeGroup: ScopeGroupUiState): string[] {
      return this.buildScopeGroupSummary(scopeGroup);
    },

    escapeHtml(raw: any): string {
      const text = raw === null || raw === undefined ? '' : String(raw);
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    isUuidString(value: any): boolean {
      return (
        typeof value === 'string' &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
          value
        )
      );
    },

    resolveTagName(uuid: string): string {
      const tags = (this as any).tags || [];
      const found = tags.find(
        (tagItem: any) => tagItem && tagItem.uuid === uuid
      );
      return found?.name || uuid;
    },

    formatTagIdsAsNames(tagIdsValue: any): string {
      const rawArray = Array.isArray(tagIdsValue)
        ? tagIdsValue
        : tagIdsValue
        ? [tagIdsValue]
        : [];

      return rawArray
        .map((uuid: any) =>
          typeof uuid === 'string' ? this.resolveTagName(uuid) : ''
        )
        .filter(Boolean)
        .join(', ');
    },

    getExtraComponents(entityScope: EntityScopeKey): Record<string, any> {
      if (entityScope === 'view') return {};
      return {
        id: {
          component: EntityComboFilter,
          attributes: { value: 'id' },
          label: 'ID',
          props: { entityType: entityScope },
        },
      };
    },

    getEntityFilterCount(scopeGroup: ScopeGroupUiState, entityScope: EntityScopeKey | AdvancedOptionKey): number {
      const filters = scopeGroup.localFilters[entityScope];
      if (!filters || typeof filters !== 'object') return 0;
      return Object.entries(filters).filter(([, v]) => {
        if (v === undefined || v === null) return false;
        if (typeof v === 'string' && v.trim() === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
      }).length;
    },

    getEntityScopeLabel(scopeKey: EntityScopeKey): string {
      const entityKey = `entity.${scopeKey}`;
      const translated = this.$t(entityKey) as string;

      return translated && translated !== entityKey
        ? translated
        : scopeKey.charAt(0).toUpperCase() + scopeKey.slice(1);
    },

    createEmptyScopeGroup(): ScopeGroupUiState {
      return {
        localFilters: {
          device: {},
          property: {},
          connector: {},
          view: {},
          reading: {},
          regex: { attribute: '', regex: '' },
          dsl: { dsl: '' },
        },
        entityMenuOpen: {
          device: false,
          property: false,
          connector: false,
          view: false,
        },
        moreMenuOpen: false,
        enabledAdvancedOptions: [],
        advancedMenuOpen: { reading: false, regex: false, dsl: false },
      };
    },

    addScopeGroup() {
      (this as any).scopeGroups.push(this.createEmptyScopeGroup());
    },

    removeScopeGroup(groupIndex: number) {
      const vm = this as any;
      vm.scopeGroups.splice(groupIndex, 1);

      if (vm.scopeGroups.length === 0) {
        vm.scopeGroups.push(this.createEmptyScopeGroup());
      }
    },

    isAdvancedOptionEnabled(
      scopeGroup: ScopeGroupUiState,
      optionKey: AdvancedOptionKey
    ) {
      return scopeGroup.enabledAdvancedOptions.includes(optionKey);
    },

    toggleAdvancedOption(groupIndex: number, optionKey: AdvancedOptionKey) {
      const vm = this as any;
      const scopeGroup: ScopeGroupUiState = vm.scopeGroups[groupIndex];

      const alreadyEnabled =
        scopeGroup.enabledAdvancedOptions.includes(optionKey);
      if (alreadyEnabled) {
        this.disableAdvancedOption(groupIndex, optionKey);
        return;
      }

      scopeGroup.enabledAdvancedOptions = [
        ...scopeGroup.enabledAdvancedOptions,
        optionKey,
      ];
      scopeGroup.moreMenuOpen = false;
      scopeGroup.advancedMenuOpen[optionKey] = true;

      if (optionKey === 'reading') {
        Vue.set(scopeGroup.localFilters, 'reading', {});
      }
      if (optionKey === 'regex') {
        Vue.set(scopeGroup.localFilters, 'regex', {
          attribute: scopeGroup.localFilters.regex?.attribute ?? '',
          regex: scopeGroup.localFilters.regex?.regex ?? '',
        });
      }
      if (optionKey === 'dsl') {
        Vue.set(scopeGroup.localFilters, 'dsl', {
          dsl: scopeGroup.localFilters.dsl?.dsl ?? '',
        });
      }
    },

    disableAdvancedOption(groupIndex: number, optionKey: AdvancedOptionKey) {
      const vm = this as any;
      const scopeGroup: ScopeGroupUiState = vm.scopeGroups[groupIndex];

      scopeGroup.enabledAdvancedOptions =
        scopeGroup.enabledAdvancedOptions.filter(
          (enabledKey: AdvancedOptionKey) => enabledKey !== optionKey
        );

      scopeGroup.advancedMenuOpen[optionKey] = false;

      if (optionKey === 'reading')
        Vue.set(scopeGroup.localFilters, 'reading', {});
      if (optionKey === 'regex')
        Vue.set(scopeGroup.localFilters, 'regex', { attribute: '', regex: '' });
      if (optionKey === 'dsl')
        Vue.set(scopeGroup.localFilters, 'dsl', { dsl: '' });
    },

    buildEntityBlocksForSave(localFilters: ScopeLocalFilters) {
      return {
        device: localFilters.device,
        property: localFilters.property,
        connector: localFilters.connector,
        view: localFilters.view,
      };
    },

    buildPayload(): any[] {
      return (this as any).scopeGroups.map((scopeGroup: ScopeGroupUiState) => {
        const entityBlocks = this.buildEntityBlocksForSave(
          scopeGroup.localFilters
        );
        const groupPayload: any = { ...entityBlocks };

        if (scopeGroup.enabledAdvancedOptions.includes('reading')) {
          groupPayload.reading = scopeGroup.localFilters.reading;
        }

        if (scopeGroup.enabledAdvancedOptions.includes('regex')) {
          groupPayload.regex = {
            attribute: (scopeGroup.localFilters.regex?.attribute ?? '').trim(),
            regex: (scopeGroup.localFilters.regex?.regex ?? '').trim(),
          };
        }

        if (scopeGroup.enabledAdvancedOptions.includes('dsl')) {
          groupPayload.dsl = {
            dsl: (scopeGroup.localFilters.dsl?.dsl ?? '').toString(),
          };
        }

        return groupPayload;
      });
    },

    emitUpdatedValue() {
      const vm = this as any;
      if (vm.isHydratingFromIncomingValue) return;

      const payload = this.buildPayload();
      const serialized = JSON.stringify(payload);
      if (serialized === vm.lastEmittedPayload) return;
      vm.lastEmittedPayload = serialized;

      vm.$emit('input', payload);
    },

    hydrateUiFromValue(incomingValue: any) {
      const vm = this as any;
      vm.isHydratingFromIncomingValue = true;

      const incomingGroupsArray = Array.isArray(incomingValue)
        ? incomingValue
        : [incomingValue || {}];

      vm.scopeGroups = incomingGroupsArray.map((incomingGroup: any) => {
        const scopeGroup = this.createEmptyScopeGroup();
        const safeIncoming = incomingGroup || {};

        const safeDevice =
          safeIncoming.device && typeof safeIncoming.device === 'object'
            ? { ...safeIncoming.device }
            : {};
        const safeProperty =
          safeIncoming.property && typeof safeIncoming.property === 'object'
            ? { ...safeIncoming.property }
            : {};
        const safeConnector =
          safeIncoming.connector && typeof safeIncoming.connector === 'object'
            ? { ...safeIncoming.connector }
            : {};
        const safeView =
          safeIncoming.view && typeof safeIncoming.view === 'object'
            ? { ...safeIncoming.view }
            : {};

        Vue.set(scopeGroup.localFilters, 'device', safeDevice);
        Vue.set(scopeGroup.localFilters, 'property', safeProperty);
        Vue.set(scopeGroup.localFilters, 'connector', safeConnector);
        Vue.set(scopeGroup.localFilters, 'view', safeView);

        const enabledAdvancedOptions: AdvancedOptionKey[] = [];

        if (safeIncoming.reading) {
          enabledAdvancedOptions.push('reading');

          Vue.set(
            scopeGroup.localFilters,
            'reading',
            { ...safeIncoming.reading }
          );
        }

        if (safeIncoming.regex) {
          enabledAdvancedOptions.push('regex');
          Vue.set(scopeGroup.localFilters, 'regex', {
            attribute: safeIncoming.regex.attribute ?? '',
            regex: safeIncoming.regex.regex ?? '',
          });
        }

        if (safeIncoming.dsl) {
          enabledAdvancedOptions.push('dsl');
          Vue.set(scopeGroup.localFilters, 'dsl', {
            dsl: safeIncoming.dsl.dsl ?? '',
          });
        }

        scopeGroup.enabledAdvancedOptions = enabledAdvancedOptions;
        return scopeGroup;
      });

      if (vm.scopeGroups.length === 0) {
        vm.scopeGroups = [this.createEmptyScopeGroup()];
      }

      vm.$nextTick(() => {
        vm.isHydratingFromIncomingValue = false;
        vm.lastEmittedPayload = JSON.stringify(this.buildPayload());
      });
    },

    formatValueForSummary(value: any): string {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean')
        return String(value);

      if (Array.isArray(value)) {
        return value.map(this.formatValueForSummary).filter(Boolean).join(', ');
      }

      if (typeof value === 'object') {
        const objectValue: any = value;
        if ('uuid' in objectValue && typeof objectValue.uuid === 'string')
          return objectValue.uuid;
        if ('id' in objectValue) return String(objectValue.id);

        return Object.values(objectValue)
          .map(this.formatValueForSummary)
          .filter(Boolean)
          .join(', ');
      }

      return String(value);
    },

    summarizeFilterBlock(blockLabel: string, blockObject: any): string {
      if (!blockObject || typeof blockObject !== 'object') return '';

      const summaryEntries = Object.entries(blockObject)
        .filter(([, entryValue]) => {
          if (entryValue === undefined || entryValue === null) return false;
          if (typeof entryValue === 'string' && entryValue.trim() === '')
            return false;
          return true;
        })
        .map(([entryKey, entryValue]) => {
          if (entryKey === 'tagIds') {
            const tagNames = this.formatTagIdsAsNames(entryValue);
            const safeTagNames = this.escapeHtml(tagNames);
            const tagLabel = this.escapeHtml(
              this.$t('rule.summary.tag') as string
            );
            return safeTagNames ? `${tagLabel}: ${safeTagNames}` : '';
          }

          const safeKey = this.escapeHtml(entryKey);
          const safeValue = this.escapeHtml(
            this.formatValueForSummary(entryValue)
          );
          return safeValue ? `${safeKey}: ${safeValue}` : '';
        })
        .filter(Boolean);

      return summaryEntries.length
        ? `<span class="scope-summary-label">${this.escapeHtml(
            blockLabel
          )}</span> (${summaryEntries.join(' · ')})`
        : '';
    },

    buildScopeGroupSummary(scopeGroup: ScopeGroupUiState): string[] {
      const summaryLines: string[] = [];

      const deviceSummary = this.summarizeFilterBlock(
        this.$t('rule.summary.device') as string,
        scopeGroup.localFilters?.device
      );
      if (deviceSummary) summaryLines.push(deviceSummary);

      const propertySummary = this.summarizeFilterBlock(
        this.$t('rule.summary.property') as string,
        scopeGroup.localFilters?.property
      );
      if (propertySummary) summaryLines.push(propertySummary);

      const connectorSummary = this.summarizeFilterBlock(
        this.$t('rule.summary.connector') as string,
        scopeGroup.localFilters?.connector
      );
      if (connectorSummary) summaryLines.push(connectorSummary);

      const viewSummary = this.summarizeFilterBlock(
        this.$t('rule.summary.view') as string,
        scopeGroup.localFilters?.view
      );
      if (viewSummary) summaryLines.push(viewSummary);

      if (scopeGroup.enabledAdvancedOptions.includes('reading')) {
        const readingSummary = this.summarizeFilterBlock(
          this.$t('rule.summary.reading') as string,
          scopeGroup.localFilters?.reading
        );
        if (readingSummary) summaryLines.push(readingSummary);
      }

      if (scopeGroup.enabledAdvancedOptions.includes('regex')) {
        const regexAttribute = (
          scopeGroup.localFilters?.regex?.attribute ?? ''
        ).trim();
        const regexPattern = (
          scopeGroup.localFilters?.regex?.regex ?? ''
        ).trim();

        if (regexAttribute || regexPattern) {
          const safePair = this.escapeHtml(
            [regexAttribute, regexPattern].filter(Boolean).join(' ~ ')
          );
          summaryLines.push(
            `<span class="scope-summary-label">${this.escapeHtml(
              this.$t('rule.summary.regex') as string
            )}</span> (${safePair})`
          );
        }
      }

      if (scopeGroup.enabledAdvancedOptions.includes('dsl')) {
        const dslExpression = (scopeGroup.localFilters?.dsl?.dsl ?? '')
          .toString()
          .trim();
        if (dslExpression) {
          summaryLines.push(
            `<span class="scope-summary-label">${this.escapeHtml(
              this.$t('rule.summary.dsl') as string
            )}</span> (${this.escapeHtml(dslExpression)})`
          );
        }
      }

      return summaryLines;
    },
  },

  watch: {
    value: {
      deep: true,
      handler(newValue: any) {
        (this as any).hydrateUiFromValue(newValue);
      },
    },
    scopeGroups: {
      deep: true,
      handler() {
        (this as any).emitUpdatedValue();
      },
    },
  },
});
